'use client';

import { useState, useMemo } from 'react';
import { cn } from '../../lib/utils';
import { ProjectCard } from './project-card';
import { ProjectFilters } from './project-filters';
import { Icon } from '@iconify/react';
import gsap from 'gsap';
import { useEffect, useRef } from 'react';
import { type Project } from '@/app/lib/data';
import { useTranslation } from 'react-i18next';

/**
 * ProjectsGrid component
 * Displays a grid of project cards with filtering options
 * Enhanced with animations and styling consistent with the rest of the portfolio
 */
export interface ProjectsGridProps {
  projects: Project[];
  className?: string;
}

export function ProjectsGrid({ projects, className }: ProjectsGridProps) {
  const { t } = useTranslation();
  // State for active filter
  const [activeFilter, setActiveFilter] = useState<string | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const iconRef = useRef<HTMLDivElement>(null);

  // Get unique categories from projects
  const categories = useMemo(() => {
    const uniqueCategories = new Set<string>();

    projects.forEach(project => {
      if (project.category) {
        uniqueCategories.add(project.category);
      }
    });

    return Array.from(uniqueCategories);
  }, [projects]);

  // Filter projects based on active filter
  const filteredProjects = useMemo(() => {
    if (!activeFilter) return projects;

    return projects.filter(project => project.category === activeFilter);
  }, [projects, activeFilter]);

  // Initial animation
  useEffect(() => {
    const tl = gsap.timeline();

    if (containerRef.current) gsap.set(containerRef.current, { opacity: 0 });
    tl.to(containerRef.current, { opacity: 1, duration: 0.4 });

    if (headerRef.current) {
      tl.fromTo(headerRef.current, { opacity: 0, y: 10 }, { opacity: 1, y: 0, duration: 0.4 }, "-=0.2");
    }

    if (iconRef.current) {
      gsap.to(iconRef.current, { y: -5, duration: 2, repeat: -1, yoyo: true, ease: "sine.inOut" });
    }
  }, []);

  // Filter change animation
  useEffect(() => {
    if (containerRef.current) {
      // The project cards are the last child
      const cardsContainer = containerRef.current.lastElementChild;
      if (cardsContainer) {
        gsap.fromTo(cardsContainer.children,
          { opacity: 0, scale: 0.95 },
          { opacity: 1, scale: 1, duration: 0.4, stagger: 0.05, ease: "power2.out" }
        );
      }
    }
  }, [filteredProjects]);

  if (!projects || projects.length === 0) {
    return (
      <div className="text-center py-10">
        <p className="text-slate-600 dark:text-slate-400">{t('Projects.noProjects')}</p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={cn('space-y-6', className)}
    >
      <div
        ref={headerRef}
        className="flex items-center gap-2 mb-2"
      >
        <div ref={iconRef}>
          <Icon
            icon="carbon:catalog"
            className="h-5 w-5 text-slate-600 dark:text-slate-400"
          />
        </div>
        <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-200">{t('Projects.all')}</h2>
        <div className="h-px flex-grow bg-slate-200 dark:bg-slate-700/50"></div>
      </div>

      {categories.length > 0 && (
        <ProjectFilters
          categories={categories}
          activeFilter={activeFilter}
          onFilterChange={setActiveFilter}
        />
      )}

      <div
        className="grid grid-cols-1 md:grid-cols-2 gap-6"
      >
        {filteredProjects.map((project) => (
          <div
            key={project._id}
            className="transition-transform hover:-translate-y-1 duration-200"
          >
            <ProjectCard
              title={project.title}
              description={project.description}
              imageSrc={project.mainImage ?? ''}
              href={`/projects/${project.slug?.current || ''}`}
              iconName={project.iconName}
              technologies={project.technologies || []}
              translationKey={project.translationKey}
            />
          </div>
        ))}
      </div>
    </div>
  );
} 